// components/recipes/workflow/DayOfWeekToggle.tsx
//
// Seven circular day toggles (Mon..Sun) plus quick presets, replacing the old
// vertical checkbox list in the "On Certain Days" modal. Filled = selected.
// Empty selection shows a subtle red ring instead of a callout block.
import React from 'react'
import classNames from 'classnames'

export type Day = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

const DAYS: { key: Day; short: string }[] = [
    { key: 'monday', short: 'M' },
    { key: 'tuesday', short: 'T' },
    { key: 'wednesday', short: 'W' },
    { key: 'thursday', short: 'T' },
    { key: 'friday', short: 'F' },
    { key: 'saturday', short: 'S' },
    { key: 'sunday', short: 'S' },
]

const PRESETS: { label: string; days: Day[] }[] = [
    { label: 'Weekdays', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] },
    { label: 'Weekends', days: ['saturday', 'sunday'] },
    { label: 'Every day', days: DAYS.map((d) => d.key) },
]

const sameSet = (a: Day[], b: Day[]) => a.length === b.length && a.every((d) => b.includes(d))

interface DayOfWeekToggleProps {
    value: Day[]
    onChange: (days: Day[]) => void
    /** Render the empty-state ring to nudge the user to pick at least one day. */
    showError?: boolean
}

export const DayOfWeekToggle = ({ value, onChange, showError }: DayOfWeekToggleProps) => {
    const toggle = (day: Day) => {
        onChange(value.includes(day) ? value.filter((d) => d !== day) : [...value, day])
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => {
                    const active = sameSet(value, p.days)
                    return (
                        <button
                            key={p.label}
                            type="button"
                            onClick={() => onChange(p.days)}
                            className={classNames(
                                'rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
                                active
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
                            )}
                        >
                            {p.label}
                        </button>
                    )
                })}
            </div>

            <div
                className={classNames(
                    'flex flex-wrap gap-x-0.5 gap-y-1 rounded-lg p-0.5',
                    showError && value.length === 0 && 'ring-2 ring-red-300',
                )}
            >
                {DAYS.map((d, i) => {
                    const selected = value.includes(d.key)
                    return (
                        <button
                            key={`${d.key}-${i}`}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggle(d.key)}
                            className={classNames(
                                'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold',
                                'transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60',
                                selected
                                    ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700',
                            )}
                        >
                            {d.short}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export { DAYS as DAY_LIST }
